import { useCallback, useEffect, useState } from 'react';
import {
  AiAbortedError,
  describeError,
  AiContractError,
  type DescribedError,
} from '../connect/errors.ts';

export type AiResource<T> =
  | { readonly status: 'loading' }
  | {
      readonly status: 'error';
      readonly error: DescribedError;
      readonly code: string | undefined;
      readonly httpStatus: number | undefined;
    }
  | { readonly status: 'ready'; readonly data: T };

/**
 * Loads one provider read. A failure is shown as an error; it is never
 * replaced by placeholder data. The request is abandoned when the component
 * unmounts, the inputs change or `reload()` is called.
 */
export function useAiResource<T>(
  load: (signal: AbortSignal) => Promise<T>,
  deps: readonly unknown[],
): { readonly state: AiResource<T>; readonly reload: () => void } {
  const [nonce, setNonce] = useState(0);
  const [state, setState] = useState<AiResource<T>>({ status: 'loading' });
  const reload = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    const controller = new AbortController();
    setState({ status: 'loading' });
    load(controller.signal).then(
      (data) => {
        if (!controller.signal.aborted) setState({ status: 'ready', data });
      },
      (error: unknown) => {
        if (controller.signal.aborted || error instanceof AiAbortedError) return;
        const contract = error instanceof AiContractError ? error : null;
        setState({
          status: 'error',
          error: describeError(error),
          code: contract?.detailCode,
          httpStatus: contract?.status,
        });
      },
    );
    return () => controller.abort();
  }, [nonce, ...deps]);

  return { state, reload };
}
